/**
 * TeamDetailPage — Full detail view for a single Team primitive.
 *
 * Route: /ferp-core/workforce/teams/:teamCode
 *
 * Shows team identity, type, hierarchy, membership, quotas, presence, and workload.
 * Deep-linkable from task assignments or org chart navigation.
 *
 * DRR-233 Track B: Enhanced with team presence and workspace data.
 *
 * Team data: useGetTeamByCodeQuery (direct), fallback to useListTeamsQuery.
 * Member presence: useGetTeamPresenceQuery (DRR-061).
 * Workspace data: useGetTeamWorkspaceQuery (aggregated).
 */

import { Link, useParams } from 'react-router-dom';
import {
  Alert,
  Anchor,
  Badge,
  Breadcrumbs,
  Container,
  Divider,
  Group,
  Paper,
  Progress,
  RingProgress,
  SimpleGrid,
  Skeleton,
  Stack,
  Table,
  Text,
  ThemeIcon,
  Title,
  Tooltip,
} from '@mantine/core';
import {
  IconAlertCircle,
  IconCircleFilled,
  IconUsers,
  IconUserCheck,
  IconUserOff,
} from '@tabler/icons-react';

import {
  GqlTeamType,
  useListTeamsQuery,
  useGetTeamByCodeQuery,
  useGetTeamPresenceQuery,
  useGetActorsByTeamQuery,
  GqlPresenceStatus,
} from '@/generated/graphql';
import { useActorNames } from '@/hooks/useActorNames';

// ============================================================================
// Helpers
// ============================================================================

const TEAM_TYPE_COLOR: Record<string, string> = {
  [GqlTeamType.SQUAD]: 'cyan',
  [GqlTeamType.GROUP]: 'green',
  [GqlTeamType.DEPARTMENT]: 'blue',
  [GqlTeamType.DIVISION]: 'purple',
  [GqlTeamType.PROJECT]: 'orange',
  [GqlTeamType.CUSTOM]: 'gray',
};

const PRESENCE_COLOR: Record<string, string> = {
  [GqlPresenceStatus.ONLINE]: 'green',
  [GqlPresenceStatus.AWAY]: 'yellow',
  [GqlPresenceStatus.OFFLINE]: 'gray',
};

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

/** Presence indicator dot */
function PresenceIndicator({ status }: { status: GqlPresenceStatus }) {
  const color = PRESENCE_COLOR[status] ?? 'gray';
  return (
    <Tooltip label={status}>
      <ThemeIcon size="xs" color={color} variant="filled" radius="xl">
        <IconCircleFilled size={8} />
      </ThemeIcon>
    </Tooltip>
  );
}

// ============================================================================
// Component
// ============================================================================

export function TeamDetailPage() {
  const { teamCode } = useParams<{ teamCode: string }>();

  // Try direct query first (DRR-233)
  const { data: teamData, loading: teamLoading, error: teamError } = useGetTeamByCodeQuery({
    variables: { code: teamCode ?? '' },
    skip: !teamCode,
  });

  // Fallback to list query if direct query not available
  const { data: listData, loading: listLoading, error: listError } = useListTeamsQuery({
    variables: { limit: 500 },
    skip: !!teamData?.getTeam,
  });

  // Team presence data (DRR-061)
  const team = teamData?.getTeam ?? listData?.listTeams?.find((t) => t.code === teamCode);
  const { data: presenceData, loading: presenceLoading } = useGetTeamPresenceQuery({
    variables: { teamId: team?.id ?? '' },
    skip: !team?.id,
  });

  // Team members with full actor data
  const { data: membersData, loading: membersLoading } = useGetActorsByTeamQuery({
    variables: { teamId: team?.id ?? '' },
    skip: !team?.id,
  });

  const { resolveActorName, loading: actorNamesLoading } = useActorNames();

  // Calculate presence stats
  const presenceStats = {
    total: presenceData?.getTeamPresence?.length ?? 0,
    online: presenceData?.getTeamPresence?.filter(p => p.status === GqlPresenceStatus.ONLINE).length ?? 0,
    away: presenceData?.getTeamPresence?.filter(p => p.status === GqlPresenceStatus.AWAY).length ?? 0,
    offline: presenceData?.getTeamPresence?.filter(p => p.status === GqlPresenceStatus.OFFLINE).length ?? 0,
  };

  // Calculate workload stats from member actors
  const workloadStats = {
    totalCapacity: membersData?.getActorsByTeam?.reduce((sum, a) => sum + (a.maxCapacity ?? 0), 0) ?? 0,
    currentLoad: membersData?.getActorsByTeam?.reduce((sum, a) => sum + (a.workload ?? 0), 0) ?? 0,
  };
  const utilizationPercent = workloadStats.totalCapacity > 0
    ? Math.round((workloadStats.currentLoad / workloadStats.totalCapacity) * 100)
    : 0;

  // Build presence map for quick lookup
  const presenceMap = new Map(
    presenceData?.getTeamPresence?.map(p => [p.actorId, p.status]) ?? []
  );

  const loading = teamLoading || listLoading;
  const error = teamError ?? listError;
  const data = listData;

  // -------------------------------------------------------------------------
  // Loading / error states
  // -------------------------------------------------------------------------

  if (loading) {
    return (
      <Container size="xl" py="xl">
        <Stack gap="md">
          <Skeleton height={28} width={300} />
          <Skeleton height={20} width={200} />
          <SimpleGrid cols={{ base: 1, sm: 3 }} mt="md">
            {Array.from({ length: 4 }).map((_, i) => (
              <Skeleton key={i} height={80} radius="md" />
            ))}
          </SimpleGrid>
        </Stack>
      </Container>
    );
  }

  if (error) {
    return (
      <Container size="xl" py="xl">
        <Alert icon={<IconAlertCircle size={16} />} color="red" title="Failed to load teams">
          {error.message}
        </Alert>
      </Container>
    );
  }

  // team is already defined above (line 114)
  if (!team) {
    return (
      <Container size="xl" py="xl">
        <Alert icon={<IconAlertCircle size={16} />} color="orange" title="Not found">
          Team <code>{teamCode}</code> does not exist or you lack access.
        </Alert>
      </Container>
    );
  }

  // Resolve parent team name
  const parentTeam = team.parentTeamId
    ? data?.listTeams.find((t) => t.id === team.parentTeamId)
    : null;

  // -------------------------------------------------------------------------
  // Render
  // -------------------------------------------------------------------------

  return (
    <Container size="xl" py="lg">
      <Stack gap="lg">
        {/* Breadcrumb */}
        <Breadcrumbs>
          <Anchor component={Link} to="/ferp-core/workforce" size="sm">
            Workforce
          </Anchor>
          <Anchor component={Link} to="/ferp-core/workforce?tab=teams" size="sm">
            Teams
          </Anchor>
          <Text size="sm">{team.name}</Text>
        </Breadcrumbs>

        {/* Header */}
        <Group justify="space-between" align="flex-start">
          <Group gap="sm">
            <IconUsers size={28} />
            <div>
              <Title order={2}>{team.name}</Title>
              {team.description && (
                <Text c="dimmed" size="sm">
                  {team.description}
                </Text>
              )}
            </div>
          </Group>
          <Group gap="xs">
            <Badge
              color={TEAM_TYPE_COLOR[team.teamType] ?? 'gray'}
              variant="light"
              size="lg"
            >
              {team.teamType}
            </Badge>
            <Badge
              color={team.active ? 'green' : 'red'}
              variant="light"
              size="lg"
            >
              {team.active ? 'Active' : 'Archived'}
            </Badge>
          </Group>
        </Group>

        {/* Stat cards */}
        <SimpleGrid cols={{ base: 2, sm: 4 }}>
          <Paper p="sm" withBorder radius="md">
            <Text size="xs" c="dimmed">Code</Text>
            <Text size="sm" fw={600} ff="monospace">{team.code}</Text>
          </Paper>
          <Paper p="sm" withBorder radius="md">
            <Text size="xs" c="dimmed">Level</Text>
            <Text size="sm" fw={600}>{team.level}</Text>
          </Paper>
          <Paper p="sm" withBorder radius="md">
            <Group gap="xs">
              <IconUsers size={16} />
              <div>
                <Text size="xs" c="dimmed">Members</Text>
                <Text size="sm" fw={600}>{team.stakeholderIds.length}</Text>
              </div>
            </Group>
          </Paper>
          <Paper p="sm" withBorder radius="md">
            <Text size="xs" c="dimmed">Parent Team</Text>
            <Text size="sm" fw={600}>
              {parentTeam ? (
                <Anchor
                  component={Link}
                  to={`/ferp-core/workforce/teams/${parentTeam.code}`}
                  size="sm"
                >
                  {parentTeam.name}
                </Anchor>
              ) : '—'}
            </Text>
          </Paper>
        </SimpleGrid>

        {/* Presence & Workload (DRR-233 Track B) */}
        <SimpleGrid cols={{ base: 1, sm: 2 }}>
          {/* Team Presence */}
          <Paper p="md" withBorder radius="md">
            <Text fw={600} size="sm" mb="sm">Team Presence</Text>
            {presenceLoading ? (
              <Skeleton height={60} />
            ) : (
              <Group gap="lg">
                <Group gap="xs">
                  <ThemeIcon size="sm" color="green" variant="light">
                    <IconUserCheck size={14} />
                  </ThemeIcon>
                  <div>
                    <Text size="xs" c="dimmed">Online</Text>
                    <Text size="lg" fw={600} c="green">{presenceStats.online}</Text>
                  </div>
                </Group>
                <Group gap="xs">
                  <ThemeIcon size="sm" color="yellow" variant="light">
                    <IconCircleFilled size={14} />
                  </ThemeIcon>
                  <div>
                    <Text size="xs" c="dimmed">Away</Text>
                    <Text size="lg" fw={600}>{presenceStats.away}</Text>
                  </div>
                </Group>
                <Group gap="xs">
                  <ThemeIcon size="sm" color="gray" variant="light">
                    <IconUserOff size={14} />
                  </ThemeIcon>
                  <div>
                    <Text size="xs" c="dimmed">Offline</Text>
                    <Text size="lg" fw={600} c="dimmed">{presenceStats.offline}</Text>
                  </div>
                </Group>
              </Group>
            )}
          </Paper>

          {/* Team Workload */}
          <Paper p="md" withBorder radius="md">
            <Text fw={600} size="sm" mb="sm">Team Capacity</Text>
            {membersLoading ? (
              <Skeleton height={60} />
            ) : (
              <Stack gap="xs">
                <Group justify="space-between">
                  <Text size="sm">
                    {workloadStats.currentLoad} / {workloadStats.totalCapacity} tasks
                  </Text>
                  <Text size="sm" fw={600} c={utilizationPercent > 80 ? 'red' : utilizationPercent > 60 ? 'orange' : 'green'}>
                    {utilizationPercent}% utilized
                  </Text>
                </Group>
                <Progress
                  value={utilizationPercent}
                  color={utilizationPercent > 80 ? 'red' : utilizationPercent > 60 ? 'orange' : 'green'}
                  size="lg"
                  radius="xl"
                />
              </Stack>
            )}
          </Paper>
        </SimpleGrid>

        {/* Leadership */}
        <Paper p="md" withBorder radius="md">
          <Stack gap="sm">
            <Text fw={600} size="sm">Leadership</Text>
            <SimpleGrid cols={{ base: 1, sm: 2 }}>
              <div>
                <Text size="xs" c="dimmed">Team Lead</Text>
                <Text size="sm">
                  {actorNamesLoading ? '...' : resolveActorName(team.teamLeadId)}
                </Text>
              </div>
              <div>
                <Text size="xs" c="dimmed">Deputy Lead</Text>
                <Text size="sm">
                  {actorNamesLoading ? '...' : resolveActorName(team.deputyLeadId)}
                </Text>
              </div>
            </SimpleGrid>
          </Stack>
        </Paper>

        {/* Members (Enhanced with presence & workload) */}
        <Paper p="md" withBorder radius="md">
          <Stack gap="sm">
            <Group justify="space-between">
              <Text fw={600} size="sm">
                Members ({team.stakeholderIds.length})
              </Text>
              <Badge size="sm" color="green" variant="light">
                {presenceStats.online} online
              </Badge>
            </Group>
            {team.stakeholderIds.length === 0 ? (
              <Text size="sm" c="dimmed">No members yet.</Text>
            ) : (
              <Table>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th style={{ width: 40 }}>Status</Table.Th>
                    <Table.Th>Name</Table.Th>
                    <Table.Th>Role</Table.Th>
                    <Table.Th>Workload</Table.Th>
                    <Table.Th>Actor ID</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {membersData?.getActorsByTeam?.map((actor) => {
                    const presence = presenceMap.get(actor.id) ?? GqlPresenceStatus.OFFLINE;
                    const loadPercent = actor.maxCapacity > 0
                      ? Math.round((actor.workload / actor.maxCapacity) * 100)
                      : 0;
                    return (
                      <Table.Tr key={actor.id}>
                        <Table.Td>
                          <PresenceIndicator status={presence} />
                        </Table.Td>
                        <Table.Td>
                          <Anchor
                            component={Link}
                            to={`/ferp-core/workforce/actors/${actor.id}`}
                            size="sm"
                          >
                            {actor.name}
                          </Anchor>
                        </Table.Td>
                        <Table.Td>
                          <Text size="sm" c="dimmed">
                            {actor.primaryRoleId ?? actor.role ?? '—'}
                          </Text>
                        </Table.Td>
                        <Table.Td>
                          <Group gap="xs">
                            <Progress
                              value={loadPercent}
                              color={loadPercent > 80 ? 'red' : loadPercent > 60 ? 'orange' : 'green'}
                              size="sm"
                              radius="xl"
                              style={{ width: 60 }}
                            />
                            <Text size="xs" c="dimmed">
                              {actor.workload}/{actor.maxCapacity}
                            </Text>
                          </Group>
                        </Table.Td>
                        <Table.Td>
                          <Text size="xs" c="dimmed" ff="monospace">{actor.id}</Text>
                        </Table.Td>
                      </Table.Tr>
                    );
                  }) ?? team.stakeholderIds.map((actorId) => (
                    <Table.Tr key={actorId}>
                      <Table.Td>
                        <PresenceIndicator status={presenceMap.get(actorId) ?? GqlPresenceStatus.OFFLINE} />
                      </Table.Td>
                      <Table.Td>
                        <Anchor
                          component={Link}
                          to={`/ferp-core/workforce/actors/${actorId}`}
                          size="sm"
                        >
                          {actorNamesLoading ? '...' : resolveActorName(actorId)}
                        </Anchor>
                      </Table.Td>
                      <Table.Td><Text size="sm" c="dimmed">—</Text></Table.Td>
                      <Table.Td><Text size="sm" c="dimmed">—</Text></Table.Td>
                      <Table.Td>
                        <Text size="xs" c="dimmed" ff="monospace">{actorId}</Text>
                      </Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            )}
          </Stack>
        </Paper>

        {/* Budget */}
        {team.budgetAllocated != null && (
          <Paper p="md" withBorder radius="md">
            <Text fw={600} size="sm" mb="xs">Budget</Text>
            <Text size="sm">
              ${team.budgetAllocated.toLocaleString()} allocated
            </Text>
          </Paper>
        )}

        {/* Metadata */}
        <Divider />
        <SimpleGrid cols={{ base: 1, sm: 2 }}>
          <div>
            <Text size="xs" c="dimmed">Team ID</Text>
            <Text size="sm" ff="monospace">{team.id}</Text>
          </div>
          <div>
            <Text size="xs" c="dimmed">Hierarchy Path</Text>
            <Text size="sm" ff="monospace">{team.path || '/'}</Text>
          </div>
          <div>
            <Text size="xs" c="dimmed">Created</Text>
            <Text size="sm">{formatDate(team.createdAt)}</Text>
          </div>
          <div>
            <Text size="xs" c="dimmed">Last Updated</Text>
            <Text size="sm">{formatDate(team.updatedAt)}</Text>
          </div>
        </SimpleGrid>
      </Stack>
    </Container>
  );
}

export default TeamDetailPage;
